// Delete-agent confirm modal. Matches the "type 'delete' to confirm" pattern
// used elsewhere in the app (feedback_delete_confirm_word.md). Bundles a
// warning about what the cascade tears down on the Cloudflare side.

import { useState } from 'react';

export default function DeleteAgentModal({ agent, onCancel, onConfirm, isPending, error, warnings }) {
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toLowerCase() === 'delete';

  const managed = Boolean(agent?.cf_tunnel_id || agent?.cfTunnelId);
  const name    = agent?.name || agent?.label || agent?.id || agent?.agent_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">Delete PBX host</h3>
        <p className="mt-2 text-sm text-gray-600">
          This will permanently remove{' '}
          <span className="font-semibold">{name}</span> from Verso.
        </p>
        {managed && (
          <ul className="mt-3 list-disc space-y-0.5 pl-5 text-xs text-gray-500">
            <li>Cloudflare Tunnel + DNS CNAME will be deleted</li>
            <li>Cloudflare Access application + service token will be revoked</li>
            <li>The on-prem cloudflared daemon on the PBX host will stop connecting</li>
            <li>Agent API key is invalidated immediately (heartbeats will 401)</li>
          </ul>
        )}
        {!managed && (
          <p className="mt-3 text-xs text-gray-500">
            This is a legacy manually-configured agent. Only the Verso database row
            is removed; you&rsquo;ll need to clean up the tunnel + Access app manually
            on the Cloudflare side.
          </p>
        )}
        <p className="mt-3 text-sm text-gray-600">
          Type <span className="font-mono font-semibold text-red-600">delete</span> to confirm:
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="mt-2 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
          placeholder="delete"
          autoFocus
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        {warnings && warnings.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <div className="font-semibold">Cloudflare cleanup warnings:</div>
            <ul className="mt-1 list-disc pl-4">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            The agent row was removed regardless — clean the CF side manually.
          </div>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || isPending}
            className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Deleting…' : 'Delete host'}
          </button>
        </div>
      </div>
    </div>
  );
}
